import React, { useState, useEffect } from 'react';

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import useMediaQuery from '@mui/material/useMediaQuery';

import Alert from 'shared/atoms/Alert';
import Button from 'shared/atoms/Button';
import Form from 'shared/atoms/Form';
import FormInput from 'shared/atoms/FormFields/FormInput';
import HorizontalRule from 'shared/atoms/HorizontalRule';
import Select from 'shared/atoms/Select';
import Snackbar from 'shared/atoms/Snackbar';
import T from 'shared/atoms/Typography';

import { membersRoles } from 'shared/const';
import { useForm } from 'shared/utils';
import theme from 'shared/themes/default';

import GroupsOverview from '../GroupsOverview';

import { useOrganizationMembers } from '../../context';

const StyledBlock = styled.div`
  text-align: left;
  width: 25%;
  ${theme.breakpoints.tablet} {
    width: 50%;
  }
  ${theme.breakpoints.mobile} {
    width: 100%;
  }
`;

const ButtonWrapper = styled.div`
  ${theme.breakpoints.mobile} {
    align-self: flex-start;
    margin-top: 10px;
  }
`;

const HeaderWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  ${theme.breakpoints.mobile} {
    align-items: start;
    flex-direction: column-reverse;
    flex-wrap: wrap;
  }
`;

const StyledHorizontalRule = styled(HorizontalRule)`
  margin-top: 30px;
`;

const MessageBoxWrapper = styled.div`
  margin-top: 10px;
`;

const MAX_NAME_LENGTH = 70;

const ManageOrCreateGroups = () => {
  const {
    databaseUser,
    groups: { createGroupFormStatus, onCreateGroup, setCreateGroupFormStatus },
    isBackOffice,
    registeredMembers,
    setManageGroupsOpen,
  } = useOrganizationMembers();

  const userId = databaseUser?.id;
  const isDesktopView = useMediaQuery(theme.breakpoints.desktop);
  const [openMessageBox, setOpenMessagebox] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState(
    isBackOffice ? 'all' : userId
  );

  const { t } = useTranslation();

  const managerOptions = [];

  if (!isBackOffice) {
    managerOptions.push({
      key: userId,
      label: t('administrateUsers.myGroups'),
      value: userId,
    });
  }

  managerOptions.push({
    key: 'all',
    label: t('administrateUsers.allGroups'),
    value: 'all',
  });

  registeredMembers
    .filter(
      (member) =>
        (isBackOffice || member.id !== userId) &&
        (member.role === membersRoles.admin ||
          member.role === membersRoles.manager)
    )
    .map(({ user: manager }) => ({
      value: manager.id,
      label: manager.name,
      key: manager.id,
    }))
    .forEach((manager) => managerOptions.push(manager));

  const {
    changeValues,
    disableSubmit,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    properties,
    submitting,
    values,
  } = useForm({
    changesRequired: true,
    fields: {
      name: {
        default: '',
        maxLength: MAX_NAME_LENGTH,
        required: true,
      },
      description: {
        default: '',
      },
    },
    onSubmit: (values) => {
      onCreateGroup({
        ...values,
        ownerId: isBackOffice ? selectedManagerId : userId,
      });
    },
  });

  useEffect(() => {
    if (createGroupFormStatus) {
      setOpenMessagebox(true);

      // hide the message after 10 sec for desktop
      if (isDesktopView) {
        setTimeout(() => {
          setOpenMessagebox(false);
          setCreateGroupFormStatus(null);
        }, 10000);
      }

      if (createGroupFormStatus.success) {
        changeValues({ name: '', description: '' });
      }
    }
  }, [createGroupFormStatus]);

  const snackbarType = createGroupFormStatus?.success ? 'success' : 'error';
  const notificationMessage = createGroupFormStatus?.success
    ? t('administrateUsers.createGroupSuccess')
    : t('administrateUsers.createGroupfailed');

  const isSubmitDisabled =
    disableSubmit || (isBackOffice && selectedManagerId === 'all');
  return (
    <>
      <HeaderWrapper>
        {isBackOffice ? (
          <div>
            <T
              color={theme.palette.lightPure}
              fontWeight="medium"
              sx={{ mt: theme.spacing.m3 }}
              variant="subtitle"
            >
              {t('administrateUsers.groups')}
            </T>
            <T
              color={theme.palette.disabledLight}
              fontWeight="medium"
              sx={{ mb: theme.spacing.m3, mt: theme.spacing.m1 }}
              variant="paragraph"
            >
              {t('administrateUsers.filterGroups')}
            </T>
            <Select
              hasAction
              hasBackground
              id="manager-picker"
              items={managerOptions}
              name="manager-picker"
              onChange={({ value }) => {
                setSelectedManagerId(value);
              }}
              sx={{ width: '300px' }}
              theme="dark"
              value={selectedManagerId}
            />
          </div>
        ) : (
          <div>
            <T
              color={theme.palette.lightPure}
              fontWeight="medium"
              sx={{ mt: theme.spacing.m3 }}
              variant="headingS"
            >
              {t('administrateUsers.createNewGroup')}
            </T>
            <T
              color={theme.palette.disabledLight}
              fontWeight="medium"
              sx={{ mb: theme.spacing.m3, mt: theme.spacing.m1 }}
              variant="paragraph"
            >
              {t('administrateUsers.createNewGroupSubTitle')}
            </T>
          </div>
        )}
        <ButtonWrapper>
          <Button
            ariaLabel={t('administrateUsers.returnToMembers')}
            iconLeft={ChevronLeftRoundedIcon}
            onClick={() => {
              setCreateGroupFormStatus(null);
              setManageGroupsOpen(false);
            }}
            theme="dark"
            type="secondary"
          >
            {t('administrateUsers.returnToMembers')}
          </Button>
        </ButtonWrapper>
      </HeaderWrapper>
      {isBackOffice && (
        <div>
          <StyledHorizontalRule />
          <T
            color={theme.palette.lightPure}
            fontWeight="medium"
            sx={{ mt: theme.spacing.m3 }}
            variant="headingS"
          >
            {t('administrateUsers.createNewGroup')}
          </T>
          <T
            color={theme.palette.disabledLight}
            fontWeight="medium"
            sx={{ mb: theme.spacing.m3, mt: theme.spacing.m1 }}
            variant="paragraph"
          >
            {t('administrateUsers.createNewGroupSubTitle')}
          </T>
          <T
            color={theme.palette.disabledLight}
            fontWeight="medium"
            sx={{ mb: theme.spacing.m3, mt: theme.spacing.m1 }}
            variant="paragraph"
          >
            {t('administrateUsers.selectManagerToCreateGroup')}
          </T>
        </div>
      )}
      <StyledBlock>
        <Form onSubmit={handleSubmit}>
          <FormInput
            disabled={selectedManagerId === 'all' ? true : undefined}
            errors={errors}
            handleBlur={handleBlur}
            handleChange={handleChange}
            id="name"
            label={t('administrateUsers.groupName')}
            name="name"
            placeholder={t('administrateUsers.groupNamePlaceHolder')}
            properties={properties}
            sx={{ mb: theme.spacing.m2 }}
            theme="dark"
            values={values}
          />
          <FormInput
            disabled={selectedManagerId === 'all' ? true : undefined}
            errors={errors}
            handleBlur={handleBlur}
            handleChange={handleChange}
            id="description"
            label={t('administrateUsers.description')}
            minRows={4}
            multiline
            name="description"
            placeholder={t('administrateUsers.descriptionPlaceHolder')}
            properties={properties}
            sx={{ mb: theme.spacing.m2 }}
            theme="dark"
            values={values}
          />
          <Button
            ariaLabel="create-group-button"
            disabled={isSubmitDisabled}
            loading={submitting}
            loadingOnIcon={submitting}
            submit
            theme="dark"
            type="primary"
          >
            {t('administrateUsers.createGroup')}
          </Button>
        </Form>
      </StyledBlock>
      {openMessageBox &&
        (isDesktopView ? (
          <MessageBoxWrapper>
            <Alert
              message={notificationMessage}
              severity={snackbarType}
              theme="light"
            />
          </MessageBoxWrapper>
        ) : (
          <Snackbar
            message={notificationMessage}
            open={openMessageBox}
            onClose={() => {
              setCreateGroupFormStatus(null);
              setOpenMessagebox(false);
            }}
            type={snackbarType}
          />
        ))}
      <GroupsOverview selectedManagerId={selectedManagerId} />
    </>
  );
};

export default ManageOrCreateGroups;
